/**
 * Bridges the stored auth token into React so permission state survives a mid-session
 * token swap.
 *
 * The token is not immutable for the life of a page: `encodeData` rewrites it after
 * `POST /Auth/extend-session`. A snapshot captured into `useState` would go stale at
 * that moment and keep gating on the old claim set. `useSyncExternalStore` re-reads on
 * every notification instead.
 */

import { useSyncExternalStore } from 'react';

import { getJwtSnapshot } from './authority';
import { subscribeToTokenChange } from './token-events';
import { EMPTY_SNAPSHOT, type IPermissionSnapshot } from './types';

/** This app does not server-render, but React requires the third argument. */
const getServerSnapshot = (): IPermissionSnapshot => EMPTY_SNAPSHOT;

/**
 * The current permission snapshot derived from the stored token. Synchronous, so it is
 * correct on the very first render and nothing needs a loading state to gate on it.
 *
 * `getJwtSnapshot` is memoized on the raw envelope string, which `useSyncExternalStore`
 * requires — an unstable reference here re-renders forever.
 */
export const useJwtSnapshot = (): IPermissionSnapshot =>
	useSyncExternalStore(subscribeToTokenChange, getJwtSnapshot, getServerSnapshot);
